// frontend/src/pages/CategoryPage.js
import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Play, Clock, Lock, List, BookOpen } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import apiService from "../services/apiService";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ContractBadge from "../components/common/ContractBadge";
import LanguageBadge from "../components/common/LanguageBadge";
import ShareButton from "../components/common/ShareButton";
import QuizCard from "../components/quiz/QuizCard";
import useSocialMeta from "../hooks/useSocialMeta";

// Helper function to add language suffix to image URLs
// Example: /images/videos/123.jpg with lang 'de' becomes /images/videos/123_de.jpg
const getLocalizedImageUrl = (url, language) => {
  if (!url || !language) return url;

  // Insert language suffix before file extension
  const lastDotIndex = url.lastIndexOf('.');
  if (lastDotIndex === -1) return url; // No extension found

  return url.slice(0, lastDotIndex) + '_' + language + url.slice(lastDotIndex);
};

const VideoCard = ({ video, categorySlug }) => {
  const { t, language } = useLanguage();
  const { hasAccess } = useAuth();
  const canAccess = hasAccess(video.contract);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <Link
      to={canAccess ? `/video/${video.slug}` : "#"}
      state={{ categorySlug }}
      className={`group bg-white rounded-lg shadow-md overflow-hidden transition-transform duration-200 border border-gray-200 ${
        canAccess
          ? "hover:scale-105 cursor-pointer hover:shadow-lg"
          : "opacity-60 cursor-not-allowed pointer-events-none"
      }`}
    >
      <div className="aspect-video bg-gray-200 relative">
        <img
          // Try language-specific image first, e.g., image_de.jpg for German
          src={getLocalizedImageUrl(video.imageUrl || video.thumbnailUrl, language)}
          alt={t(video.title)}
          className="w-full h-full object-cover"
          onError={(e) => {
            // Fallback chain for images:
            const currentSrc = e.target.src;
            const hasLangSuffix = currentSrc.includes('_' + language + '.');

            if (hasLangSuffix) {
              // Step 1: Remove language suffix and try generic image
              const genericUrl = currentSrc.replace('_' + language + '.', '.');
              e.target.src = genericUrl;
            } else if (video.imageUrl && video.thumbnailUrl && currentSrc.includes(video.imageUrl.split('/').pop())) {
              // Step 2: Try thumbnailUrl if we were using imageUrl
              const localizedThumb = getLocalizedImageUrl(video.thumbnailUrl, language);
              if (localizedThumb) {
                e.target.src = localizedThumb;
              } else {
                e.target.style.display = "none";
              }
            } else if (video.thumbnailUrl && currentSrc.includes('_' + language)) {
              // Step 3: Try generic thumbnailUrl
              e.target.src = video.thumbnailUrl;
            } else {
              // Step 4: Hide image if all attempts failed
              e.target.style.display = "none";
            }
          }}
        />

        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity flex items-center justify-center">
          <Play className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>

        {!canAccess && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <Lock className="w-8 h-8 text-white" />
          </div>
        )}

        <div className="absolute bottom-2 left-2 flex items-center space-x-2">
          {video.language && <LanguageBadge language={video.language} />}
          <div className="bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded flex items-center">
            <Clock className="w-3 h-3 mr-1" />
            {formatDuration(video.duration)}
          </div>
        </div>

        {video.contract !== 'Free' && (
          <div className="absolute top-2 right-2">
            <ContractBadge contract={video.contract} />
          </div>
        )}
      </div>

      <div className="p-4">
        <h3 className="text-base font-semibold text-[#002855] mb-1">
          {t(video.title)}
        </h3>
      </div>
    </Link>
  );
};

const PlaylistCard = ({ playlist, categorySlug }) => {
  const { t, language } = useLanguage();
  const { hasAccess } = useAuth();
  const canAccess = hasAccess(playlist.contract);

  return (
    <Link
      to={canAccess ? `/playlist/${playlist.slug}` : "#"}
      state={{ categorySlug }}
      className={`group bg-white rounded-lg shadow-md overflow-hidden transition-transform duration-200 border border-gray-200 ${
        canAccess
          ? "hover:scale-105 cursor-pointer hover:shadow-lg"
          : "opacity-60 cursor-not-allowed pointer-events-none"
      }`}
    >
      <div className="aspect-video bg-gray-200 relative">
        <img
          // Try language-specific image first, e.g., image_de.jpg for German
          src={getLocalizedImageUrl(playlist.thumbnailUrl, language)}
          alt={t(playlist.title)}
          className="w-full h-full object-cover"
          onError={(e) => {
            // Fallback chain for playlist images:
            const currentSrc = e.target.src;
            const hasLangSuffix = currentSrc.includes('_' + language + '.');

            if (hasLangSuffix) {
              // Step 1: Remove language suffix and try generic image
              const genericUrl = currentSrc.replace('_' + language + '.', '.');
              e.target.src = genericUrl;
            } else if (playlist.imageUrl && playlist.imageUrl !== playlist.thumbnailUrl) {
              // Step 2: Try imageUrl as alternative (with language suffix)
              const localizedImage = getLocalizedImageUrl(playlist.imageUrl, language);
              e.target.src = localizedImage;
            } else if (playlist.imageUrl && currentSrc.includes('_' + language)) {
              // Step 3: Try generic imageUrl
              e.target.src = playlist.imageUrl;
            } else {
              // Step 4: Hide image if all attempts failed
              e.target.style.display = "none";
            }
          }}
        />

        {!canAccess && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <Lock className="w-8 h-8 text-white" />
          </div>
        )}

        {playlist.contract !== 'Free' && (
          <div className="absolute top-2 right-2">
            <ContractBadge contract={playlist.contract} />
          </div>
        )}

        <div className="absolute bottom-2 left-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded flex items-center">
          <List className="w-3 h-3 mr-1" />
          {playlist.videoCount || "0"}
        </div>
      </div>

      <div className="p-4">
        <h3 className="text-base font-semibold text-[#002855] mb-1">
          {t(playlist.title)}
        </h3>
      </div>
    </Link>
  );
};

const CoachHandbookTile = () => {
  const { isCoach, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleClick = (e) => {
    if (!isCoach) {
      e.preventDefault();
      if (!isAuthenticated) {
        alert("Bitte einloggen — das Coach Handbook ist nur für Coaches verfügbar.");
      } else {
        alert("Das Coach Handbook ist nur für freigeschaltete burnair Coaches verfügbar.");
      }
      return;
    }
    navigate("/coach-handbook");
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`group bg-white rounded-lg shadow-md overflow-hidden transition-transform duration-200 border border-gray-200 text-left w-full ${
        isCoach
          ? "hover:scale-105 cursor-pointer hover:shadow-lg"
          : "opacity-90 cursor-not-allowed"
      }`}
    >
      <div className="aspect-video bg-gradient-to-br from-[#002855] to-[#00A3E0] relative flex items-center justify-center">
        <BookOpen className="w-16 h-16 text-white opacity-90" />
        {!isCoach && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <Lock className="w-10 h-10 text-white" />
          </div>
        )}
        <div className="absolute top-2 right-2 bg-[#84BD00] text-white text-xs px-2 py-1 rounded font-semibold">
          Coaches
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-base font-semibold text-[#002855] mb-1">
          Coach Handbook
        </h3>
        <p className="text-xs text-gray-500">
          Leitfaden für burnair XC Camps und Reisen
        </p>
      </div>
    </button>
  );
};

const CategoryPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [categoryData, setCategoryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { t } = useLanguage();

  // Social media meta tags
  useSocialMeta({
    title: categoryData ? t(categoryData.category.title) : null,
    description: categoryData ? 'Browse videos and playlists in this category on burnair Academy' : null,
    image: categoryData ? categoryData.category.imageUrl : null,
    url: `/category/${slug}`,
    type: 'website'
  });

  // Redirect special category slugs to their dedicated pages
  useEffect(() => {
    if (slug === 'events') {
      navigate('/events', { replace: true });
      return;
    }
    if (slug === 'articles') {
      navigate('/articles', { replace: true });
      return;
    }

    loadCategory();

    // Scroll to top after a small delay to ensure content has rendered
    const scrollTimer = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);

    return () => clearTimeout(scrollTimer);
  }, [slug, navigate]);

  const loadCategory = async () => {
    try {
      const response = await apiService.getCategoryBySlug(slug);
      if (response.success) {
        setCategoryData(response.data);
      } else {
        setError(response.message);
      }
    } catch (error) {
      console.error("Error loading category:", error);
      setError("Failed to load category");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link
          to="/"
          className="flex items-center text-[#00A3E0] hover:text-[#002855] mb-4 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          {t({ en: "Back to Categories", de: "Zurück zu Kategorien" })}
        </Link>
        <div className="text-center text-[#DA291C]">Error: {error}</div>
      </div>
    );
  }

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Link
          to="/"
          className="flex items-center text-[#00A3E0] hover:text-[#002855] mb-4 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          {t({ en: "Back to Categories", de: "Zurück zu Kategorien" })}
        </Link>

        <h1 className="text-3xl font-bold text-[#002855]">
          {t(categoryData.category.title)}
        </h1>
      </div>

      <div className="space-y-8">
        {slug === "instructors" && (
          <section>
            <h2 className="text-2xl font-semibold text-[#002855] mb-4">
              {t({ en: "Coach Resources", de: "Coach-Ressourcen" })}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <CoachHandbookTile />
            </div>
          </section>
        )}

        {categoryData.quizzes?.length > 0 && (
          <section>
            <h2 className="text-2xl font-semibold text-[#002855] mb-4">
              {t({ en: "Quizzes", de: "Quiz" })}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryData.quizzes.map((quiz) => (
                <QuizCard key={quiz.quiz_id} quiz={quiz} />
              ))}
            </div>
          </section>
        )}

        {categoryData.playlists?.length > 0 && (
          <section>
            <h2 className="text-2xl font-semibold text-[#002855] mb-4">
              {t({ en: "Course Playlists", de: "Kurs-Wiedergabelisten" })}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryData.playlists.map((playlist) => (
                <PlaylistCard key={playlist.playlist_id} playlist={playlist} categorySlug={slug} />
              ))}
            </div>
          </section>
        )}

        {categoryData.videos?.length > 0 && (
          <section>
            <h2 className="text-2xl font-semibold text-[#002855] mb-4">
              {t({ en: "Individual Videos", de: "Einzelne Videos" })}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryData.videos.map((video) => (
                <VideoCard key={video.video_id} video={video} categorySlug={slug} />
              ))}
            </div>
          </section>
        )}

        {!categoryData.videos?.length && !categoryData.playlists?.length && !categoryData.quizzes?.length && slug !== "instructors" && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">
              {t({
                en: "No content available in this category.",
                de: "Kein Inhalt in dieser Kategorie verfügbar.",
              })}
            </p>
          </div>
        )}
      </div>
    </main>
  );
};

export default CategoryPage;
